import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { CaretDown, FunnelSimple, SlidersHorizontal, X } from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FilterGroup = "domain" | "status" | "goal_type";

/** Client-side sort keys for the goal list — no field here triggers a refetch. */
export type GoalOrderBy =
  | "status"
  | "created_at-desc"
  | "created_at-asc"
  | "title-asc"
  | "title-desc"
  | "phase-asc"
  | "domain-asc"
  | "goal_type-asc"
  | "behavior_subject-asc";

export interface OrderingOption {
  label: string;
  value: GoalOrderBy;
}

export const GOAL_ORDERING_OPTIONS: OrderingOption[] = [
  { label: "Status (default)", value: "status" },
  { label: "Date Added (Newest)", value: "created_at-desc" },
  { label: "Date Added (Oldest)", value: "created_at-asc" },
  { label: "Name (A–Z)", value: "title-asc" },
  { label: "Name (Z–A)", value: "title-desc" },
  { label: "Phase (Baseline → Maintenance)", value: "phase-asc" },
  { label: "Domain (A–Z)", value: "domain-asc" },
  { label: "Goal Type (A–Z)", value: "goal_type-asc" },
  { label: "Who It's For (A–Z)", value: "behavior_subject-asc" },
];

export interface FilterOption {
  /** Unique identifier e.g. "domain:communication" */
  id: string;
  /** Human-readable label e.g. "Communication" */
  label: string;
  /** Which filter group this option belongs to */
  group: FilterGroup;
}

export interface ActiveFilter {
  /** Unique identifier for the filter — passed back to onDeleteFilter */
  id: string;
  /** Human-readable label shown inside the chip e.g. "Domain: Communication" */
  label: string;
}

export interface GoalGridFilterRowProps {
  /** Number of goals matching the current filters */
  goalCount: number;
  /** Total number of goals from the API, before any FE filtering */
  totalGoalCount: number;
  /** Available filter options grouped by domain, status, and goal type */
  filterOptions?: FilterOption[];
  /** Currently active filter chips. When empty the chips area and Clear button are hidden. */
  activeFilters?: ActiveFilter[];
  /** Whether archived goals are currently visible */
  showArchived?: boolean;
  /** Current client-side sort applied to the goal list */
  orderBy?: GoalOrderBy;
  /** Options shown in the Display menu. Defaults to GOAL_ORDERING_OPTIONS. */
  orderingOptions?: OrderingOption[];
  /** Called when a filter option is selected from the menu */
  onAddFilter?: (option: FilterOption) => void;
  /** Called when a filter chip's delete icon is clicked */
  onDeleteFilter: (filterId: string) => void;
  /** Called when the Show Archived / Hide Archived button is clicked */
  onToggleArchived: () => void;
  /** Called when the Clear button is clicked — parent clears all active filters */
  onClearFilters: () => void;
  /** Called when a Display (sort) option is selected — purely client-side */
  onOrderByChange?: (value: GoalOrderBy) => void;
  /** Additional MUI sx overrides for the root container */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GROUP_LABELS: Record<FilterGroup, string> = {
  domain: "Domain",
  status: "Status",
  goal_type: "Goal Type",
};

const FILTER_GROUPS: FilterGroup[] = ["domain", "status", "goal_type"];

// ---------------------------------------------------------------------------
// Internal subcomponents
// ---------------------------------------------------------------------------

interface FiltersMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  filterOptions: FilterOption[];
  activeFilterIds: Set<string>;
  onAddFilter?: (option: FilterOption) => void;
}

/**
 * Dropdown menu for selecting filters to apply to the goal grid.
 * Options are grouped by domain, status, and goal type.
 * Already active filters are disabled in the menu.
 */
const FiltersMenu: React.FC<FiltersMenuProps> = ({
  anchorEl,
  open,
  onClose,
  filterOptions,
  activeFilterIds,
  onAddFilter,
}) => (
  <Menu
    id="goal-filters-menu"
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    MenuListProps={{ "aria-label": "Filters", dense: true }}
  >
    {FILTER_GROUPS.flatMap((group, groupIdx) => {
      const groupOptions = filterOptions.filter((o) => o.group === group);
      if (!groupOptions.length) return [];

      const items: React.ReactNode[] = [];

      if (groupIdx > 0) {
        items.push(<Divider key={`divider-${group}`} />);
      }

      items.push(
        <ListSubheader key={`header-${group}`} disableSticky>
          {GROUP_LABELS[group]}
        </ListSubheader>
      );

      groupOptions.forEach((option) => {
        items.push(
          <MenuItem
            key={option.id}
            disabled={activeFilterIds.has(option.id)}
            onClick={() => {
              onAddFilter?.(option);
              onClose();
            }}
          >
            {option.label}
          </MenuItem>
        );
      });

      return items;
    })}
  </Menu>
);

interface DisplayMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  orderingOptions: OrderingOption[];
  orderBy?: GoalOrderBy;
  onOrderByChange?: (value: GoalOrderBy) => void;
}

/**
 * Dropdown menu for choosing how the goal list is sorted — mirrors the
 * "Ordering" box + Select layout used by ANTableFilters on the Libraries page.
 * Client-side only — selecting an option never triggers a refetch.
 */
const DisplayMenu: React.FC<DisplayMenuProps> = ({
  anchorEl,
  open,
  onClose,
  orderingOptions,
  orderBy,
  onOrderByChange,
}) => {
  const theme = useTheme();
  const orderingLabelLookup = orderingOptions.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

  return (
    <Menu
      id="goal-display-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ "aria-labelledby": "open-display" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="301px"
        padding={theme.spacing(1, 7)}
      >
        <Typography variant="subtitle2">Ordering</Typography>
        <TextField
          select
          sx={{ width: "168px" }}
          SelectProps={{ renderValue: (selected) => orderingLabelLookup[selected as string] }}
          value={orderBy}
          onChange={(e) => {
            onOrderByChange?.(e.target.value as GoalOrderBy);
            onClose();
          }}
        >
          {orderingOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Menu>
  );
};

interface ActiveFilterChipsProps {
  filters: ActiveFilter[];
  onDelete: (filterId: string) => void;
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({ filters, onDelete }) => (
  <Stack
    component="ul"
    aria-label="Active filters"
    direction="row"
    alignItems="center"
    gap={3}
    flexWrap="wrap"
    p={0}
    m={0}
    sx={{ listStyle: "none" }}
  >
    {filters.map((filter) => (
      <Box component="li" key={filter.id}>
        <Chip
          label={filter.label}
          variant="outlined"
          size="small"
          onDelete={() => onDelete(filter.id)}
          deleteIcon={<X size={14} aria-label={`Remove filter: ${filter.label}`} />}
        />
      </Box>
    ))}
  </Stack>
);

// ---------------------------------------------------------------------------
// GoalGridFilterRow
// ---------------------------------------------------------------------------

/**
 * Filter toolbar for the goal grid.
 * Renders Filters dropdowns, active filter chips,
 * a Show/Hide Archived toggle, a filtered goal count, and a Clear button.
 *
 * @param props
 * @param props.goalCount - Number of goals matching the current FE filters
 * @param props.totalGoalCount - Total goals returned from the API (unfiltered)
 * @param props.filterOptions - Available filter options grouped by domain, status, and goalType
 * @param props.activeFilters - Currently active filter chips
 * @param props.showArchived - Whether archived goals are visible
 * @param props.onAddFilter - Called when a filter option is selected
 * @param props.onDeleteFilter - Called when a filter chip is removed
 * @param props.onToggleArchived - Called when the Show/Hide Archived button is clicked
 * @param props.onClearFilters - Called when the Clear button is clicked
 */
const GoalGridFilterRow: React.FC<GoalGridFilterRowProps> = ({
  goalCount,
  totalGoalCount,
  filterOptions = [],
  activeFilters = [],
  showArchived = false,
  orderBy = "status",
  orderingOptions = GOAL_ORDERING_OPTIONS,
  onAddFilter,
  onDeleteFilter,
  onToggleArchived,
  onClearFilters,
  onOrderByChange,
  sx,
}) => {
  const [filtersAnchor, setFiltersAnchor] = useState<null | HTMLElement>(null);
  const [displayAnchor, setDisplayAnchor] = useState<null | HTMLElement>(null);

  const hasActiveFilters = activeFilters.length > 0;
  const activeFilterIds = new Set(activeFilters.map((f) => f.id));

  const goalCountLabel = hasActiveFilters
    ? `${goalCount} of ${totalGoalCount} ${totalGoalCount === 1 ? "goal" : "goals"}`
    : `${totalGoalCount} ${totalGoalCount === 1 ? "goal" : "goals"}`;

  return (
    <>
      <DisplayMenu
        anchorEl={displayAnchor}
        open={Boolean(displayAnchor)}
        onClose={() => setDisplayAnchor(null)}
        orderingOptions={orderingOptions}
        orderBy={orderBy}
        onOrderByChange={onOrderByChange}
      />

      <FiltersMenu
        anchorEl={filtersAnchor}
        open={Boolean(filtersAnchor)}
        onClose={() => setFiltersAnchor(null)}
        filterOptions={filterOptions}
        activeFilterIds={activeFilterIds}
        onAddFilter={onAddFilter}
      />

      <Stack
        component="nav"
        aria-label="Goal filters"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={3}
        width="100%"
        sx={sx}
      >
        {/* Left: controls + active chips + show archived */}
        <Stack alignItems="center" flexWrap="wrap" gap={3} direction="row">
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Button
              id="open-display"
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<SlidersHorizontal size={16} />}
              endIcon={<CaretDown size={14} />}
              onClick={(e) => setDisplayAnchor(e.currentTarget)}
              aria-label="Open display options"
              aria-haspopup="true"
              aria-expanded={Boolean(displayAnchor)}
              aria-controls="goal-display-menu"
            >
              Display
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<FunnelSimple size={16} />}
              endIcon={<CaretDown size={14} />}
              onClick={(e) => setFiltersAnchor(e.currentTarget)}
              aria-label="Open filter options"
              aria-haspopup="true"
              aria-expanded={Boolean(filtersAnchor)}
              aria-controls="goal-filters-menu"
              disabled={totalGoalCount === 0}
            >
              Filters
            </Button>
          </Box>

          {hasActiveFilters && (
            <ActiveFilterChips filters={activeFilters} onDelete={onDeleteFilter} />
          )}

          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onToggleArchived}
            aria-pressed={showArchived}
          >
            {showArchived ? "Hide Archived" : "Show Archived"}
          </Button>
        </Stack>

        {/* Right: goal count + clear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {totalGoalCount > 0 ? goalCountLabel : ""}
          </Typography>

          {hasActiveFilters && (
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<X size={14} />}
              onClick={onClearFilters}
              aria-label="Clear all filters"
            >
              Clear
            </Button>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default GoalGridFilterRow;
